"use client"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import NextImage from "next/image"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import RewardsBannerMobile from "@/assets/images/account/rewards-banner-mobile.png"
import { PopLoading } from "@/components/pop-ui/pop-loading"
import { toast } from "@/components/pop-ui/pop-toast"
import { fixModuleDrawerViewport } from "@/components/ui/module-drawer"
import { PullToRefreshWrapper } from "@/components/ui/pull-to-refresh"
import { ResponsiveCarousel } from "@/components/ui/responsive-carousel"
import { useIsMobile } from "@/hooks/use-mobile"
import LocaleLink from "@/lib/i18n/locale-link"
import { cn } from "@/lib/utils"
import {
	getCurrentLanguageInClient,
	getValueFromTranslations,
	useRouterPush,
} from "@/lib/utils.client"
import { ExchangeItem } from "@/modules/activity/exchange/exchange-item"
import { ExchangeModal } from "@/modules/activity/exchange/exchange-modal"
import type { Exchange } from "@/modules/activity/exchange/schema"
import PropModal from "@/modules/activity/prop/components/prop-modal"
import { UserFormModal } from "@/modules/users/user/user-form-modal"
import { useORPC } from "@/providers/orpc-provider"
import type { UserProfile } from "../schema"
import { ModuleSectionTitle, ModuleTitle } from "./components/module-title"

export function ExchangeModule({
	// onGoRewards,
	isMemberUser,
}: {
	onGoRewards: () => void
	isMemberUser: boolean
}) {
	const { t } = useTranslation()
	const lang = getCurrentLanguageInClient()
	const orpc = useORPC()
	const queryClient = useQueryClient()
	const isMobile = useIsMobile()
	const routerPush = useRouterPush()

	const [modalMode, setModalMode] = useState<"detail" | "exchange">("detail")

	const [isModalOpen, setIsModalOpen] = useState(false)
	const [selectedExchange, setSelectedExchange] = useState<Exchange | null>(
		null,
	)
	const [isPropDetailModalOpen, setIsPropDetailModalOpen] = useState(false)
	const [selectedPropForDetail, setSelectedPropForDetail] = useState<any>(null)

	// 添加登录模态框状态
	const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
	const [pendingAction, setPendingAction] = useState<{
		type: "birthday" | "exchange"
		exchangeId?: string
	} | null>(null)

	const [currentPage, _setCurrentPage] = useState(1)
	const pageSize = 10

	const [isFirstLoad, setIsFirstLoad] = useState(true)

	const findAll = isMemberUser
		? orpc.activity.exchange.findByGroupsAndUser
		: orpc.activity.exchange.public_findByGroupsAndUser

	const {
		data: currentPointsData,
		isLoading: isLoadingCurrentPoints,
		refetch: refetchCurrentPoints,
	} = useQuery({
		...orpc.member.getCurrentPoints.queryOptions({ input: {} }),
		enabled: isMemberUser,
	})

	// Get user profile to check birthday
	const {
		data: userProfile,
		isLoading: isLoadingUserProfile,
		refetch: refetchUserProfile,
	} = useQuery({
		...(orpc as any).user.getProfile.queryOptions({ input: {} }),
		enabled: isMemberUser,
	})
	const profile = userProfile as UserProfile

	// 生日奖励（birthday-rewards）
	const {
		data: birthdayRewardsData,
		isLoading: isLoadingBirthdayRewards,
		refetch: refetchBirthdayRewards,
	} = useQuery({
		...findAll.queryOptions({
			input: { pageSize: 100, exchangeGroup: "birthday-rewards" },
		}),
	})

	// 会员优惠券（member-rewards-coupon）
	const {
		data: memberRewardsCouponData,
		isLoading: isLoadingMemberRewardsCoupon,
		refetch: refetchMemberRewardsCoupon,
	} = useQuery({
		...findAll.queryOptions({
			input: { pageSize: 100, exchangeGroup: "member-rewards-coupon" },
		}),
	})

	// 会员道具（member-rewards-prop，分页）
	const {
		data: memberRewardsPropData,
		isLoading: isLoadingMemberRewardsProp,
		refetch: refetchMemberRewardsProp,
	} = useQuery({
		...findAll.queryOptions({
			input: {
				pageSize: pageSize,
				page: currentPage,
				exchangeGroup: "member-rewards-prop",
			},
		}),
	})

	// 整体loading状态
	const isInitialLoading =
		isFirstLoad &&
		((isMemberUser && (isLoadingCurrentPoints || isLoadingUserProfile)) ||
			isLoadingBirthdayRewards ||
			isLoadingMemberRewardsCoupon ||
			isLoadingMemberRewardsProp)

	// 刷新loading状态
	const _isRefreshing =
		!isFirstLoad &&
		((isMemberUser && (isLoadingCurrentPoints || isLoadingUserProfile)) ||
			isLoadingBirthdayRewards ||
			isLoadingMemberRewardsCoupon ||
			isLoadingMemberRewardsProp)

	// 检查是否有数据需要展示
	const hasBirthdayRewards =
		birthdayRewardsData?.items && birthdayRewardsData.items.length > 0
	const hasMemberRewardsCoupon =
		memberRewardsCouponData?.items && memberRewardsCouponData.items.length > 0
	const hasMemberRewardsProp =
		memberRewardsPropData?.items && memberRewardsPropData.items.length > 0
	const hasMemberRewards = hasMemberRewardsCoupon || hasMemberRewardsProp

	// 刷新数据处理函数
	const handleRefresh = async () => {
		setIsFirstLoad(false)
		const promises: Promise<any>[] = []

		if (isMemberUser) {
			promises.push(refetchCurrentPoints())
			promises.push(refetchUserProfile())
		}

		promises.push(refetchBirthdayRewards())
		promises.push(refetchMemberRewardsCoupon())
		promises.push(refetchMemberRewardsProp())

		await Promise.all(promises)
	}

	const exchangeMutation = useMutation({
		...orpc.activity.exchange.exchangeClaim.mutationOptions(),
		onSuccess: (_data, _variables) => {
			// Capture before selectedExchange is nulled
			const exchangedItemGroup = selectedExchange?.exchangeGroup
			const hasCouponReward = selectedExchange?.grantRewards?.some(
				(reward) => reward.rewardType === "coupon",
			)
			toast.success(t("exchange_modal.exchange_successful_toast", "兌換成功！"))
			setIsModalOpen(false)
			setSelectedExchange(null) // Null out after use

			// Invalidate user profile query if it's a member user
			if (isMemberUser) {
				queryClient.invalidateQueries({
					queryKey: orpc.member.getCurrentPoints.queryOptions({
						input: {},
					}).queryKey,
				})

				// Check if the exchanged item contains a coupon reward and refresh coupon counts
				if (hasCouponReward) {
					queryClient.invalidateQueries({
						queryKey: orpc.ec.userCoupon.findMyCouponCounts.queryOptions({
							input: {},
						}).queryKey,
					})
				}
			}

			// Conditionally invalidate the specific exchange list query
			if (exchangedItemGroup) {
				let queryOptionsInput:
					| { pageSize: number; page?: number; exchangeGroup: string }
					| undefined
				if (exchangedItemGroup === "birthday-rewards") {
					queryOptionsInput = {
						pageSize: 100,
						exchangeGroup: "birthday-rewards",
					}
				} else if (exchangedItemGroup === "member-rewards-coupon") {
					queryOptionsInput = {
						pageSize: 100,
						exchangeGroup: "member-rewards-coupon",
					}
				} else if (exchangedItemGroup === "member-rewards-prop") {
					queryOptionsInput = {
						pageSize: pageSize,
						page: currentPage,
						exchangeGroup: "member-rewards-prop",
					}
				} else {
					// Log if the group is unexpected
					console.warn(
						"Unknown exchange group for invalidation:",
						exchangedItemGroup,
					)
				}

				if (queryOptionsInput) {
					queryClient.invalidateQueries({
						queryKey: orpc.activity.exchange.findByGroupsAndUser.queryOptions({
							input: queryOptionsInput,
						}).queryKey,
					})
				}
			}
		},
		onError: () => {},
	})

	const handleOpenExchangeModal = (exchangeId: string) => {
		// 在所有三个数据源中查找商品
		const item =
			birthdayRewardsData?.items?.find((it) => it.id === exchangeId) ||
			memberRewardsCouponData?.items?.find((it) => it.id === exchangeId) ||
			memberRewardsPropData?.items?.find((it) => it.id === exchangeId)

		if (item) {
			setSelectedExchange(item as unknown as Exchange) // 类型断言
			setIsModalOpen(true)
		}
	}

	function handleViewDetail(exchangeId: string) {
		const item =
			birthdayRewardsData?.items?.find((it) => it.id === exchangeId) ||
			memberRewardsCouponData?.items?.find((it) => it.id === exchangeId) ||
			memberRewardsPropData?.items?.find((it) => it.id === exchangeId)

		if (item) {
			const grantReward = item.grantRewards[0]
			const isPropReward = grantReward?.rewardType === "prop"

			if (isPropReward) {
				setSelectedPropForDetail({
					name: getValueFromTranslations(lang, grantReward.name_trans), // Placeholder
					images: grantReward.icon, // Placeholder
					howToRedeemHtml: getValueFromTranslations(
						lang,
						grantReward.description_trans,
					), // Placeholder
					description: getValueFromTranslations(
						lang,
						grantReward.releaseDescription_trans,
					),
				})
				setIsPropDetailModalOpen(true)
			} else {
				handleOpenExchangeModal(exchangeId)
				setModalMode("detail")
			}
		}
	}
	function handleExchange(exchangeId: string) {
		if (!isMemberUser) {
			handleLogin({ type: "exchange", exchangeId })
			return
		}

		// 在所有三个数据源中查找商品
		const item = (birthdayRewardsData?.items?.find(
			(it) => it.id === exchangeId,
		) ||
			memberRewardsCouponData?.items?.find((it) => it.id === exchangeId) ||
			memberRewardsPropData?.items?.find(
				(it) => it.id === exchangeId,
			)) as unknown as Exchange

		if (item) {
			const isBirthdayReward = birthdayRewardsData?.items?.some(
				(it) => it.id === exchangeId,
			)

			// 4. 生日奖励的生日月检查
			if (isBirthdayReward && isMemberUser) {
				if (!profile?.birthday) {
					toast.error(
						t(
							"exchange_modal.birthday_not_set",
							"Please set your birthday first!",
						),
					)
					return
				}

				const userBirthdayMonth = new Date(profile.birthday).getMonth() + 1
				const currentMonth = new Date().getMonth() + 1
				if (userBirthdayMonth !== currentMonth) {
					toast.error(
						t(
							"exchange_modal.birthday_month_error",
							"Please redeem the birthday gifts in your birthday month!",
						),
					)
					return
				}
			}

			// 5. 检查用户剩余可领取次数
			if (item.remainingClaimsForUser <= 0) {
				toast.error(
					t(
						"exchange_modal.claim_limit_reached_error",
						"Sorry, you have reached the claim limit for this item.",
					),
				)
				return
			}

			// 通过所有检查，打开兑换弹窗
			handleOpenExchangeModal(exchangeId)
			setModalMode("exchange")
		}
	}

	const handleCloseModal = () => {
		setIsModalOpen(false)
		setSelectedExchange(null)
	}

	const handleConfirmExchange = () => {
		if (selectedExchange) {
			exchangeMutation.mutate({ exchangeId: selectedExchange.id })
		}
	}

	// 处理登录
	const handleLogin = (action?: {
		type: "birthday" | "exchange"
		exchangeId?: string
	}) => {
		setPendingAction(action || null)
		setIsLoginModalOpen(true)
	}

	// 登录成功后的处理
	const handleLoginSuccess = () => {
		setIsLoginModalOpen(false)

		// 如果有待处理的操作，执行它
		if (pendingAction) {
			if (pendingAction.type === "birthday") {
				// 直接跳转到生日设置页面
				routerPush("/account/manage?return=exchange")
			} else if (
				pendingAction.type === "exchange" &&
				pendingAction.exchangeId
			) {
				// 执行兑换操作
				handleExchange(pendingAction.exchangeId)
			}
		}

		// 重新获取数据
		handleRefresh()

		// 清除待处理的操作
		setPendingAction(null)
	}

	const contentDom = isInitialLoading ? (
		<div className="flex items-center justify-center min-h-100">
			<PopLoading />
		</div>
	) : (
		<div className="pb-10">
			{isMemberUser && (
				<>
					<ModuleTitle title={t("points.myRewardsButton", "My Rewards")} />

					{/* Mobile layout: 积分区在上，banner区在下 */}
					<div className="md:hidden">
						{/* 积分展示区 - 移动端,通栏设计 */}
						<div
							className={cn(
								"w-full h-15 bg-[#f5f5f5] text-black flex items-center px-5",
							)}
						>
							<div className="w-full flex items-end justify-center">
								<span
									className={cn(
										"font-['DINPro-Bold'] font-bold   text-2xl 3xl:text-[2rem] leading-none text-black",
									)}
								>
									{currentPointsData?.points ?? 0}
								</span>
								<span
									className={cn(
										"font-['DINPro-Regular'] font-normal text-xs leading-3 text-black ml-2 mt-0.5",
									)}
								>
									{t("points.currentPointsSuffix")}
								</span>
							</div>
						</div>

						{/* Banner区 - 移动端：宽高比 686/284 */}
						<div
							className="mt-2 w-full aspect-686/284 relative overflow-hidden"
							style={{ marginBottom: "24px" }}
						>
							<div className="absolute inset-0 mx-4 flex items-center justify-center">
								<NextImage
									src={RewardsBannerMobile}
									alt="Rewards Banner"
									fill
									className="object-contain"
								/>
							</div>
						</div>
					</div>

					{/* Desktop layout: 积分区在左，banner区在右 */}
					<div className="hidden md:flex  gap-2.5 aspect-1034/220">
						{/* 积分展示区 - 桌面端：1:1 正方形，与banner同高 */}
						<div
							className={cn(
								"aspect-square h-full bg-[#f5f5f5] text-black flex flex-col gap-2 justify-center items-center",
							)}
						>
							<span
								className={cn(
									"font-['DINPro-Bold'] font-bold text-[2rem] leading-none text-black",
								)}
							>
								{currentPointsData?.points ?? 0}
							</span>
							<span
								className={cn(
									"font-['DINPro-Regular'] font-normal text-sm leading-3.5 text-black ml-2",
								)}
							>
								{t("points.currentPointsSuffix")}
							</span>
						</div>

						{/* Banner区 - 桌面端：宽高比 804/220 */}
						<div className="flex-1 relative overflow-hidden">
							<NextImage
								src="https://cdn-global.popmart.com/global-mobile/images/user/redeem_banner_bg1.png?x-oss-process=image/format,webp"
								alt="Rewards Banner"
								fill
								className="object-cover object-bottom"
							/>
						</div>
					</div>
				</>
			)}

			{/* Birthday rewards section - 只在有数据时显示 */}
			{hasBirthdayRewards && (
				<div className=" mt-6 md:mt-10">
					<div className="px-3 md:px-0">
						<ModuleSectionTitle
							title={t("user_account.birthday_rewards_title", "生日獎勵")}
						/>
						{(!isMemberUser || (isMemberUser && !profile?.birthday)) && (
							<div
								className={cn(
									"min-h-12 bg-[#F6EFE1]/50 mb-4 flex items-center justify-between px-4 py-2",
								)}
							>
								<span className="text-xs md:text-base text-[#725F49] font-normal leading-4">
									{t(
										"user_account.birthday_rewards_hint",
										"Reminder: Your birthday isn't set! Update it now to receive your special birthday gift.",
									)}
								</span>
								{!isMemberUser ? (
									<button
										type="button"
										onClick={() => handleLogin({ type: "birthday" })}
										className={cn(
											"bg-transparent text-sm w-17 h-8 border border-[#725F49]/50 text-[#725F49] ml-4",
										)}
									>
										GO
									</button>
								) : (
									<LocaleLink href="/account/manage?return=exchange">
										<button
											type="button"
											className={cn(
												"bg-transparent text-sm w-17 h-8 border border-[#725F49]/50 text-[#725F49] ml-4",
											)}
										>
											GO
										</button>
									</LocaleLink>
								)}
							</div>
						)}
					</div>
					<ResponsiveCarousel desktopItemsPerView={4} mobileItemsPerView={2.5}>
						{birthdayRewardsData.items.map((exchangeItem) => (
							<ExchangeItem
								key={exchangeItem.id}
								exchange={exchangeItem as unknown as Exchange}
								onExchange={handleExchange}
								onViewDetail={handleViewDetail}
								isMemberUser={isMemberUser}
								hasBirthday={!!profile?.birthday}
								onLogin={handleLogin}
							/>
						))}
					</ResponsiveCarousel>
				</div>
			)}

			<div className="mt-6 md:mt-10">
				{/* Member rewards section - 只在有数据时显示标题 */}
				{hasMemberRewards && (
					<ModuleSectionTitle
						title={t("user_account.member_rewards_title", "會員獎勵")}
						className="px-3 md:px-0 "
					/>
				)}

				{/* Member rewards coupon section */}
				{hasMemberRewardsCoupon && (
					<ResponsiveCarousel desktopItemsPerView={4} mobileItemsPerView={2.5}>
						{memberRewardsCouponData.items.map((exchangeItem) => (
							<ExchangeItem
								key={exchangeItem.id}
								exchange={exchangeItem as unknown as Exchange}
								onExchange={handleExchange}
								onViewDetail={handleViewDetail}
								isMemberUser={isMemberUser}
								hasBirthday={!!profile?.birthday}
								onLogin={handleLogin}
							/>
						))}
					</ResponsiveCarousel>
				)}

				{/* Member rewards prop section */}
				{hasMemberRewardsProp && (
					<div className="px-3 md:px-0  mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-2.5 md:gap-y-3 ">
						{memberRewardsPropData.items.map((exchangeItem) => {
							return (
								<ExchangeItem
									key={exchangeItem.id}
									exchange={exchangeItem as unknown as Exchange}
									onExchange={handleExchange}
									onViewDetail={handleViewDetail}
									isMemberUser={isMemberUser}
									hasBirthday={!!profile?.birthday}
									onLogin={handleLogin}
								/>
							)
						})}
					</div>
				)}
			</div>

			{selectedExchange && (
				<ExchangeModal
					isOpen={isModalOpen}
					onClose={handleCloseModal}
					exchange={selectedExchange}
					user={{
						availablePoints: currentPointsData?.points ?? 0,
					}}
					onConfirmExchange={handleConfirmExchange}
					isExchanging={exchangeMutation.isPending}
					readonly={!isMemberUser || modalMode === "detail"}
				/>
			)}

			{/* Prop Detail Modal */}
			{selectedPropForDetail && (
				<PropModal
					isOpen={isPropDetailModalOpen}
					onClose={() => {
						setIsPropDetailModalOpen(false)
						setSelectedPropForDetail(null)
					}}
					name={selectedPropForDetail.name}
					images={selectedPropForDetail.images}
					howToRedeemHtml={selectedPropForDetail.howToRedeemHtml}
					description={selectedPropForDetail.description}
				/>
			)}

			{/* Login Modal */}
			<UserFormModal
				open={isLoginModalOpen}
				onOpenChange={(open) => {
					setIsLoginModalOpen(open)
					if (!open) {
						setTimeout(() => {
							fixModuleDrawerViewport()
						}, 300)
					}
				}}
				onFinish={handleLoginSuccess}
				initialPageState="check"
			/>
		</div>
	)

	// 在移动端使用下拉刷新
	return isMobile ? (
		<PullToRefreshWrapper onRefresh={handleRefresh}>
			{contentDom}
		</PullToRefreshWrapper>
	) : (
		contentDom
	)
}
